import LeftNav from '@/app/LeftNav';
import { ContentErrorBoundary } from '@/components/ContentErrorBoundary';
import { SuspenseTrap } from '@/components/SuspenseTrap';
import { Button } from '@/components/ui/button';
import { refreshSnapshotsAtom } from '@rock/db-admin';
import { useSetAtom } from 'jotai';
import { Menu } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

/**
 * Shared admin shell: left nav plus a scrollable content pane. The pane wraps every page in a
 * `SuspenseTrap` (nothing here should suspend — see that file) and an error boundary (failed
 * Firestore reads reject async atoms), so a bad read shows a retry card instead of a blank screen.
 *
 * Below the `md` breakpoint the nav becomes an off-canvas drawer (toggled by the mobile top bar)
 * instead of a permanent 325px column, since that alone would eat most of a phone-width viewport.
 */
const AdminLayout = () => {
    const location = useLocation();
    const refreshSnapshots = useSetAtom(refreshSnapshotsAtom);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: close the drawer on every navigation, regardless of which location field changed
    useEffect(() => {
        setMobileNavOpen(false);
    }, [location]);

    return (
        <div className="h-screen w-screen flex flex-row items-stretch overflow-hidden">
            {mobileNavOpen && (
                <button
                    type="button"
                    aria-label="Close navigation"
                    className="fixed inset-0 z-40 bg-black/40 md:hidden"
                    onClick={() => setMobileNavOpen(false)}
                />
            )}
            <div
                className={`fixed inset-y-0 left-0 z-50 w-[280px] max-w-[85vw] overflow-y-scroll border-r-2 bg-slate-100 p-3 transition-transform duration-200 md:static md:z-auto md:w-[325px] md:max-w-none md:translate-x-0 md:transition-none ${
                    mobileNavOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                <LeftNav />
            </div>
            <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
                <div className="flex items-center gap-2 border-b p-2 md:hidden">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setMobileNavOpen(true)}
                        aria-label="Open navigation"
                    >
                        <Menu className="h-5 w-5" />
                    </Button>
                    <span className="text-sm font-semibold">Rock Admin</span>
                </div>
                <div className="relative flex-1 overflow-y-scroll">
                    <ContentErrorBoundary resetKey={location.pathname} onRetry={refreshSnapshots}>
                        <SuspenseTrap boundary="admin-content">
                            <Outlet />
                        </SuspenseTrap>
                    </ContentErrorBoundary>
                </div>
            </div>
        </div>
    );
};

export default AdminLayout;
